import { Link } from 'react-router-dom';
import { Avatar } from './Avatar';
import { StarRating } from './StarRating';
import { ShareButton } from './ShareButton';
import { useSellerRating } from '@/hooks/useSellerRating';
import { MapPin } from 'lucide-react';

const FALLBACK_IMAGES = {
  'Hot Meals': 'https://images.unsplash.com/photo-1767514579388-278d63566fd7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHw0fHxob21lbWFkZSUyMGhlYXJ0eSUyMG1lYWwlMjBydXN0aWN8ZW58MHx8fHwxNzg0NDI2MDExfDA&ixlib=rb-4.1.0&q=85',
  'Baked Goods': 'https://images.pexels.com/photos/8633662/pexels-photo-8633662.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  'Desserts': 'https://images.pexels.com/photos/5709933/pexels-photo-5709933.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  'Sauces & Preserves': 'https://images.pexels.com/photos/25003519/pexels-photo-25003519.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  'Meal Prep': 'https://images.pexels.com/photos/1640775/pexels-photo-1640775.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'
};
const DEFAULT_FALLBACK = 'https://images.pexels.com/photos/1640775/pexels-photo-1640775.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';

export const ListingCard = ({ listing, showDietaryTags = true }) => {
  const rating = useSellerRating(listing.seller_id);

  const imageSrc = listing.images?.[0]
    ? `${process.env.REACT_APP_BACKEND_URL}${listing.images[0]}`
    : (FALLBACK_IMAGES[listing.category] || DEFAULT_FALLBACK);

  return (
    <Link
      to={`/listing/${listing.id}`}
      className="group overflow-hidden rounded-md border border-[#E5E0D8] hover:-translate-y-1 block"
      style={{ backgroundColor: '#FFFFFF', transition: 'transform 0.3s' }}
      data-testid={`listing-card-${listing.id}`}
    >
      <div className="aspect-[4/3] overflow-hidden relative">
        <img
          src={imageSrc}
          alt={listing.title}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105"
          style={{ transition: 'transform 0.3s' }}
        />
        <div
          className="absolute top-2 right-2 z-10"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
          data-testid={`share-wrapper-${listing.id}`}
        >
          <ShareButton
            url={`${window.location.origin}/listing/${listing.id}`}
            title={listing.title}
            text={listing.description ? listing.description.slice(0, 100) : ''}
            variant="compact"
            testIdSuffix={`card-${listing.id}`}
          />
        </div>
      </div>
      <div className="p-4">
        <div className="flex flex-wrap gap-2 mb-2 items-center">
          {listing.cuisine && (
            <span
              className="px-2 py-0.5 rounded text-xs uppercase"
              style={{ backgroundColor: '#F2EFEB', color: '#5C5C5C', fontFamily: 'Manrope', fontSize: '10px', fontWeight: 600, letterSpacing: '0.1em', border: '1px solid #E5E0D8' }}
              data-testid={`card-cuisine-${listing.id}`}
            >
              {listing.cuisine}
            </span>
          )}
          {showDietaryTags && listing.dietary_tags?.slice(0, 2).map((tag, idx) => (
            <span
              key={idx}
              className="px-2 py-1 rounded text-xs"
              style={{ backgroundColor: '#2E5C41', color: '#FFFFFF', fontFamily: 'Manrope' }}
            >
              {tag}
            </span>
          ))}
        </div>
        <h3
          className="text-xl mb-2 tracking-tight"
          style={{ fontFamily: 'Cormorant Garamond', fontWeight: 600, color: '#1A1A1A' }}
        >
          {listing.title}
        </h3>
        <div className="flex items-center gap-2 mb-3">
          <Avatar
            name={listing.seller_name}
            imageUrl={listing.seller_profile_picture}
            size={32}
          />
          <div className="flex-1 min-w-0">
            <div style={{ fontFamily: 'Manrope', fontSize: '13px', color: '#1A1A1A', fontWeight: 500 }} className="truncate">
              {listing.seller_name}
            </div>
            {rating.total_reviews > 0 && (
              <StarRating rating={rating.average_rating} size={11} showValue totalReviews={rating.total_reviews} />
            )}
          </div>
        </div>
        <div className="flex justify-between items-center pt-3 border-t border-[#E5E0D8]">
          <span
            className="text-lg"
            style={{ fontFamily: 'Manrope', fontWeight: 600, color: '#D95A41' }}
          >
            ${listing.price}
          </span>
          <span
            className="text-sm flex items-center gap-1"
            style={{ fontFamily: 'Manrope', color: '#5C5C5C' }}
          >
            <MapPin size={12} strokeWidth={1.5} />
            {listing.location}
          </span>
        </div>
      </div>
    </Link>
  );
};
